import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, Check, ShoppingCart } from "lucide-react";

const CATEGORIES = ["produce", "dairy", "meat", "pantry", "frozen", "household", "general"];

export default function Shopping() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", quantity: "1", category: "general" });

  const load = async () => {
    const { data } = await api.get("/api/shopping");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post("/api/shopping", form);
      setForm({ name: "", quantity: "1", category: "general" });
      load();
    } catch { toast.error("Failed"); }
  };

  const toggle = async (id) => { await api.patch(`/api/shopping/${id}`); load(); };
  const remove = async (id) => { await api.delete(`/api/shopping/${id}`); load(); };

  const grouped = items.reduce((acc, it) => {
    const key = it.category || "general";
    acc[key] = acc[key] || [];
    acc[key].push(it);
    return acc;
  }, {});

  const categoryColor = { produce: "#B8CFA9", dairy: "#A8BFD1", meat: "#E9B5A0", pantry: "#E8B96B", frozen: "#A8BFD1", household: "#E9B5A0", general: "white" };

  const pendingCount = items.filter(i => !i.completed).length;

  return (
    <div data-testid="shopping-page">
      <PageHeader
        title="Shopping List"
        subtitle={`${pendingCount} item${pendingCount !== 1 ? "s" : ""} pending`}
        color="#B8CFA9"
      />

      <NBCard className="mb-6" hover={false}>
        <form onSubmit={submit} className="grid sm:grid-cols-[1fr_120px_180px_auto] gap-3">
          <NBInput testId="shopping-name" required placeholder="What's needed?" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <NBInput testId="shopping-qty" placeholder="Qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
          <NBSelect testId="shopping-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </NBSelect>
          <NBButton testId="add-shopping" type="submit" color="#B8CFA9"><Plus strokeWidth={3} className="w-4 h-4" /></NBButton>
        </form>
      </NBCard>

      {items.length === 0 ? (
        <EmptyState title="List is empty" subtitle="Add things you need to pick up." />
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <h3 className="text-lg font-black uppercase tracking-wider mb-3" style={{ fontFamily: "var(--hh-display)" }}>{cat}</h3>
              <div className="space-y-2">
                {list.map((it) => (
                  <NBCard key={it.id} color={categoryColor[cat]} className="!p-3" data-testid={`shopping-${it.id}`}>
                    <div className="flex items-center gap-3">
                      <button onClick={() => toggle(it.id)} data-testid={`toggle-shopping-${it.id}`}
                        className={`w-6 h-6 rounded border-2 border-black flex items-center justify-center shrink-0 ${it.completed ? "bg-[#E8B96B]" : "bg-white"}`}>
                        {it.completed && <Check strokeWidth={3} className="w-3 h-3" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold ${it.completed ? "line-through opacity-60" : ""}`}>{it.name}</p>
                        <p className="text-xs">Qty: {it.quantity}</p>
                      </div>
                      <button onClick={() => remove(it.id)} className="nb-btn bg-white p-1.5" data-testid={`delete-shopping-${it.id}`}>
                        <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                      </button>
                    </div>
                  </NBCard>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
